/**
 * Build configuration for the dealer portal: one app, built per brand × stage.
 *
 * Values mirror the Angular dealer-portal's `_stage.<stage>.ts` + `_brand.<brand>.ts`
 * (frontend/projects/dealer-portal/src/environments), selected by the Vite mode
 * (`vite --mode micar-prod`) instead of `fileReplacements`. An unknown mode fails the build.
 *
 * Framework-free on purpose: vite.config.ts imports it under Node, the app imports it in the
 * browser. Nothing secret belongs here — it is compiled into a public bundle.
 */

export const STAGES = {
  dev: {
    // Not `http://localhost:3000` as in Angular: the dev server proxies `/api` to the backend
    // (vite.config.ts), as App Platform routes it in staging/prod. Same-origin calls need no
    // CORS entry, and the session cookies belong to `localhost` — the host the Angular dealer
    // portal's session also lives on, so both apps share one login while they run side by side.
    API_BASE_URL: '/api',
    API_VERSION: 'v1',
    API_TIMEOUT: 30000,
    DEBUG: true,
    // The Angular dealer portal's dev server, for pages not ported yet. Under its `baseHref`
    // (`/dealer-portal/` in every configuration): with no `servePath`, `ng serve` serves there.
    LEGACY_PORTAL_URL: 'http://localhost:4200/dealer-portal',
    // The React customer portal's dev server ("Bestil" on the tyre hotel customers). Per tenant
    // subdomain in staging/prod, which the live spec has not settled (LEGACY_PARITY_RUNS.md P6).
    CUSTOMER_PORTAL_URL: 'http://localhost:4201',
    // The customer portal's dev server per tenant: browsers resolve *.localhost to loopback.
    TENANT_SITE_URL: 'http://{subdomain}.localhost:4201',
  },
  test: {
    API_BASE_URL: 'http://localhost:3001',
    API_VERSION: 'v1',
    API_TIMEOUT: 5000,
    DEBUG: true,
    LEGACY_PORTAL_URL: 'http://localhost:4200/dealer-portal',
    // The React customer portal's dev server ("Bestil" on the tyre hotel customers). Per tenant
    // subdomain in staging/prod, which the live spec has not settled (LEGACY_PARITY_RUNS.md P6).
    CUSTOMER_PORTAL_URL: 'http://localhost:4201',
    // The customer portal's dev server per tenant: browsers resolve *.localhost to loopback.
    TENANT_SITE_URL: 'http://{subdomain}.localhost:4201',
  },
  staging: {
    API_BASE_URL: '/api',
    API_VERSION: 'v1',
    API_TIMEOUT: 15000,
    DEBUG: true,
    // TODO: where the Angular dealer portal is served next to this app — depends on the live
    // App Platform spec (run 11). Until then the placeholder page names the old portal but cannot
    // link to it.
    LEGACY_PORTAL_URL: null,
    // Where the customer portal's tyre order lives ("Bestil" on the tyre hotel customers).
    // Per tenant subdomain in staging/prod; unknown until the live spec says (LEGACY_PARITY_RUNS.md P6).
    CUSTOMER_PORTAL_URL: null,
    // The brand's tenant domain decides (TENANT_SITES below).
    TENANT_SITE_URL: null,
  },
  prod: {
    API_BASE_URL: '/api',
    API_VERSION: 'v1',
    API_TIMEOUT: 10000,
    DEBUG: false,
    LEGACY_PORTAL_URL: null,
    // Where the customer portal's tyre order lives ("Bestil" on the tyre hotel customers).
    // Per tenant subdomain in staging/prod; unknown until the live spec says (LEGACY_PARITY_RUNS.md P6).
    CUSTOMER_PORTAL_URL: null,
    // The brand's tenant domain decides (TENANT_SITES below).
    TENANT_SITE_URL: null,
  },
} as const;

export const BRANDS = {
  micar: {
    SITE_TITLE: 'Micar Forhandlerportal',
    BRAND_NAME: 'MiCar',
    // The legacy login's "Sign up" pane linked here.
    SIGN_UP_URL: 'https://www.micar.dk/kontakt/',
    DOCS_URL: 'https://docs.micar.dk',
    API_DOCS_URL: 'https://api.micar.dk/docs',
    // The customer portal's tenant sites, as its own config resolves them (TENANT_DOMAINS +
    // TENANT_LABEL_SUFFIX in apps/customer-portal/src/config/app-config.ts).
    TENANT_SITES: {
      staging: 'https://{subdomain}-staging.micar.dk',
      prod: 'https://{subdomain}.micar.dk',
    },
    // Where the app is served, per stage (`/` where absent). Prod lives under a path on
    // kundeportal.micar.dk, the host that routes `/api` to the backend — so the stage's
    // same-origin `/api` still reaches it, with no CORS.
    BASE_PATHS: {
      prod: '/dealer-portal-react/',
    },
  },
  tyretrust: {
    SITE_TITLE: 'Tyretrust Forhandlerportal',
    BRAND_NAME: 'TyreTrust',
    SIGN_UP_URL: null,
    DOCS_URL: 'https://docs.tyretrust.com',
    API_DOCS_URL: 'https://api.tyretrust.com/docs',
    // The customer portal's tyretrust domain is not decided yet.
    TENANT_SITES: null,
    BASE_PATHS: null,
  },
} as const;

export type Stage = keyof typeof STAGES;
export type Brand = keyof typeof BRANDS;

/**
 * Sent as `X-App-Id` on the layout request: the backend keeps only this app's features
 * (backend/src/modules/features/schemas/layout-app-map.ts).
 */
export const APP_ID = 'dealer-portal';

export interface AppConfig {
  BRAND: Brand;
  STAGE: Stage;
  API_BASE_URL: string;
  API_VERSION: string;
  /** Default request budget, ms. */
  API_TIMEOUT: number;
  DEBUG: boolean;
  /**
   * Where the Angular dealer portal runs, for pages this app does not have yet (the legacy
   * placeholder links to the same path there). Null where that is not known yet.
   */
  LEGACY_PORTAL_URL: string | null;
  /**
   * The customer portal's base URL, for the tyre hotel's "Bestil" links (`/tyre-order?regnr=`).
   * Null where it is not known yet; the buttons then say ordering is not configured.
   */
  CUSTOMER_PORTAL_URL: string | null;
  /**
   * A tenant's own customer-portal site, with `{subdomain}` for `Tenant.subdomain` — where the
   * layout editor runs (`/editor`). Null where the brand has no tenant domain yet.
   */
  TENANT_SITE_URL: string | null;
  /**
   * The path the app is served under, with leading and trailing slash (`/` at the root). Vite's
   * `base` (asset URLs, `import.meta.env.BASE_URL`) and the router's `basepath`.
   */
  BASE_PATH: string;
  SITE_TITLE: string;
  /** Shown in the login subtitle and the footer's "Powered by". */
  BRAND_NAME: string;
  /** The login page's sign-up pane links here; no pane when null. */
  SIGN_UP_URL: string | null;
  DOCS_URL: string;
  API_DOCS_URL: string;
}

/**
 * Vite's built-in modes, mapped to a brand-stage: `vite` (dev server) runs as `development`,
 * Vitest as `test`. `production` is deliberately absent — a production build must name its
 * brand and stage.
 */
const MODE_ALIASES: Record<string, string> = {
  development: 'micar-dev',
  test: 'micar-test',
};

/** Every accepted `--mode`, e.g. `micar-prod`, `tyretrust-staging`. */
export const APP_MODES: readonly string[] = (Object.keys(BRANDS) as Brand[]).flatMap(brand =>
  (Object.keys(STAGES) as Stage[]).map(stage => `${brand}-${stage}`),
);

export function resolveAppConfig(mode: string): AppConfig {
  const resolved = MODE_ALIASES[mode] ?? mode;
  const [brand, stage] = resolved.split('-') as [string, string | undefined];

  if (!(brand in BRANDS) || !stage || !(stage in STAGES)) {
    throw new Error(
      `Unknown build mode "${mode}". Use --mode <brand>-<stage>, one of: ${APP_MODES.join(', ')}.`,
    );
  }

  const { TENANT_SITES, BASE_PATHS, ...brandConfig } = BRANDS[brand as Brand];
  const stageConfig = STAGES[stage as Stage];
  const tenantSites: Partial<Record<Stage, string>> | null = TENANT_SITES;
  const basePaths: Partial<Record<Stage, string>> | null = BASE_PATHS;
  return {
    BRAND: brand as Brand,
    STAGE: stage as Stage,
    ...stageConfig,
    ...brandConfig,
    TENANT_SITE_URL: stageConfig.TENANT_SITE_URL ?? tenantSites?.[stage as Stage] ?? null,
    BASE_PATH: basePaths?.[stage as Stage] ?? '/',
  };
}
